import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { setValidationState } from "reducers/main";
import Card from "components/Card/Card";
import {
  useGetClaimByIdQuery,
  useGetClaimValidationsQuery,
  usePatchClaimByIdMutation,
} from "services/claims-api";
import { useDispatch, useSelector } from "utils/redux";

import { useForm, Controller } from "react-hook-form";
import type { OptionType } from "components/inputs";
import { Select, TextInput } from "components/inputs";
import ClaimsPageContainer from "./ClaimsPageContainer";
import { setCurrentClaim } from "services/claimsDataSlice.ts";

const ClaimRepresentative = () => {
  const { claimId } = useParams<{ claimId: string }>();
  const dispatch = useDispatch();
  const [patchClaimById] = usePatchClaimByIdMutation();

  const {
    control,
    reset,
    getValues,
    formState: { errors },
  } = useForm(); // Default for claimant selector);

  const { data: claim, isLoading } = useGetClaimByIdQuery(
    { id: claimId },
    { skip: false },
  );

  const { data: claimValidation } = useGetClaimValidationsQuery(
    { id: claimId },
    { skip: !claimId },
  );

  useEffect(() => {
    if (!isLoading) {
      dispatch(setCurrentClaim(claim));
      reset(claim);
    }
  }, [dispatch, isLoading]);

  useEffect(() => {
    if (claim) {
      reset(claim);
    }
  }, [claim, reset]);

  useEffect(() => {
    if (claimValidation) {
      dispatch(
        setValidationState({
          errors: claimValidation.filter((e) => e.level === "ERROR"),
          warnings: claimValidation.filter((e) => e.level === "WARN"),
        }),
      );
    }
  }, [claimValidation, dispatch]);

  const handleSave = useCallback(
    async (field: string, value: unknown) => {
      try {
        await patchClaimById({
          id: claimId,
          data: field === "" ? value : { [field]: value },
          useDetail: true,
        })
          .unwrap()
          .then((result) => {
            dispatch(setCurrentClaim(result));
          });
      } catch (error) {
        console.error("Save failed:", error);
      }
    },
    [patchClaimById, claimId],
  );

  const constants = useSelector((state) => state.shared.constants);
  const focusedField = useSelector((state) => state.main.focusedField);

  return (
    <ClaimsPageContainer>
      <div key={"claim-representative-form"}>
        <div className="grid-layout gl-2 no-gap-tb">
          <div>
            <Card heading="Representative Information" cardNumber={1}>
              <Controller
                control={control}
                name={"injured_party_representative_indicator"}
                render={({ field: { name, value } }) => (
                  <Select
                    label="Select Indicator"
                    name={name}
                    errors={errors}
                    isFocused={focusedField === name}
                    options={constants?.REPRESENTATIVE_TYPES || []}
                    value={constants?.REPRESENTATIVE_TYPES?.find(
                      (option: OptionType) => option.value === value,
                    )}
                    onChange={(o: OptionType) =>
                      handleSave(
                        "injured_party_representative_indicator",
                        o.value,
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"representative_first_name"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="First Name"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "representative_first_name",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"representative_last_name"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Last Name"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "representative_last_name",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"representative_firm_name"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Company Name"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "representative_firm_name",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"representative_tin"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Tax ID"
                    placeholder={"X".repeat(9)}
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave("representative_tin", getValues(field.name))
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"representative_mailing_address_line1"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Address"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "representative_mailing_address_line1",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"representative_mailing_address_line2"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Apt / Suite"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "representative_mailing_address_line2",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"representative_city"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="City"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave("representative_city", getValues(field.name))
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"representative_state"}
                render={({ field: { name, value } }) => (
                  <Select
                    label="Select State"
                    name={name}
                    errors={errors}
                    isFocused={focusedField === name}
                    options={constants?.STATES || []}
                    value={constants?.STATES?.find(
                      (option: OptionType) => option.value === value,
                    )}
                    onChange={(o: OptionType) =>
                      handleSave("representative_state", o.value)
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"representative_zipcode"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Zip Code"
                    placeholder={"X".repeat(5)}
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "representative_zipcode",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"representative_zip_plus_4"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Zip Code + 4"
                    placeholder={"X".repeat(4)}
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "representative_zip_plus_4",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"representative_phone"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Phone Number"
                    placeholder={"X".repeat(10)}
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave("representative_phone", getValues(field.name))
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"representative_phone_extension"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Phone Extension"
                    placeholder={"X".repeat(5)}
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "representative_phone_extension",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
            </Card>
          </div>
        </div>
      </div>
    </ClaimsPageContainer>
  );
};

export default ClaimRepresentative;
